import React from 'react';
import { Button } from './Button';
import { CheckCircle2, Bell, MessageSquare, Shield, Database, Smartphone } from 'lucide-react';
import { PhoneMockup } from './PhoneMockup';
import { useNavigate } from 'react-router-dom';

export const ValueProp: React.FC = () => {
  const navigate = useNavigate();

  const handleGetApp = () => {
    navigate('/waiting-list');
  };

  return (
    <section className="py-24 bg-white overflow-hidden relative">
      <div className="container mx-auto px-6 space-y-32">
        
        {/* Block 1: Image Left, Text Right */}
        <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
          <div className="flex-1 relative w-full">
            <div className="relative z-10">
              <div className="rounded-[3rem] overflow-hidden shadow-2xl border border-gray-100">
                <img 
                  src="https://09da45aa8861e9e09f066d7bf0bbd9f0.cdn.bubble.io/f1765300090709x945264382269800300/pexels-gabby-k-5997313.jpg?_gl=1*v864bq*_gcl_au*ODcyODc3MTExLjE3NTc4MzEzNzk.*_ga*MTg4MTU3NjU1Ni4xNzU3ODMxMDg1*_ga_BFPVR2DEE2*czE3NjUyNDUyNTIkbzcyJGcxJHQxNzY1Mjk5OTExJGo1OCRsMCRoMA.." 
                  alt="African American man happily using health app on phone" 
                  className="w-full h-[400px] md:h-[500px] object-cover object-center transform hover:scale-105 transition-transform duration-700"
                />
              </div>
               
               {/* Floating Stats Cards */}
               <div className="absolute -right-8 top-12 glass-card p-4 pr-8 rounded-2xl shadow-xl animate-float-delayed hidden md:block">
                  <div className="flex items-center gap-3">
                    <div className="bg-orange-100 p-2 rounded-lg text-orange-600">
                      <Bell size={24} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-semibold">Reminder</p>
                      <p className="text-sm font-bold text-secondary">Meds in 15 mins</p>
                    </div>
                  </div>
               </div>

               <div className="absolute -left-8 bottom-12 glass-card p-4 pr-8 rounded-2xl shadow-xl animate-float hidden md:block">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 p-2 rounded-lg text-primary">
                      <MessageSquare size={24} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-semibold">Dr. Carter</p>
                      <p className="text-sm font-bold text-secondary">"Results look good..."</p>
                    </div>
                  </div>
               </div>
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[100%] bg-blue-50/50 rounded-[4rem] -z-10 -rotate-3"></div>
          </div>
          
          <div className="flex-1 space-y-8">
            <div className="inline-block px-3 py-1 bg-blue-50 text-primary rounded-full text-xs font-bold tracking-wide uppercase">Smart Health</div>
            <h2 className="text-4xl md:text-5xl font-bold text-secondary leading-tight">
              Your Personal Health Assistant, <br/>
              <span className="text-primary">Always Awake</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              The MHC Africa app isn't just for booking—it's an active partner in your health. Get reminders, chat securely, and track your vitals without leaving your home.
            </p>
            
            <div className="space-y-4 pt-4">
               {[
                 { title: 'Smart Reminders', desc: 'Never miss a pill or an appointment with automated push notifications.' },
                 { title: 'Secure Chat', desc: 'Ask quick questions to your doctor directly through the app.' }
               ].map((item, i) => (
                 <div key={i} className="flex gap-5 p-5 rounded-2xl bg-gray-50 hover:bg-white hover:shadow-lg transition-all border border-transparent hover:border-gray-100 group">
                    <div className="flex-shrink-0 mt-1 w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                      <CheckCircle2 size={20} />
                    </div>
                    <div>
                      <h4 className="font-bold text-secondary text-lg mb-1">{item.title}</h4>
                      <p className="text-gray-500 text-sm">{item.desc}</p>
                    </div>
                 </div>
               ))}
            </div>

            <div className="pt-6">
               <Button className="shadow-xl shadow-primary/20 gap-2" onClick={handleGetApp}>
                <Smartphone size={18} />
                Join Waiting List
               </Button>
            </div>
          </div>
        </div>

        {/* Block 2: Text Left, Image Right */}
        <div className="flex flex-col-reverse lg:flex-row items-center gap-16 lg:gap-24">
           <div className="flex-1 space-y-8">
            <div className="inline-block px-3 py-1 bg-purple-50 text-purple-600 rounded-full text-xs font-bold tracking-wide uppercase">Why Use The App?</div>
            <h2 className="text-4xl md:text-5xl font-bold text-secondary leading-tight">
              Carry Your Medical <br/>History <span className="text-primary">In Your Pocket</span>
            </h2>
            <p className="text-lg text-gray-600">
              Stop carrying physical files. With the MHC App, your prescriptions, lab results, and history are encrypted and stored on your device.
            </p>
            
            <div className="grid grid-cols-1 gap-4">
                {[
                    { title: "Bank-Grade Security", desc: "Your health data is encrypted end-to-end.", icon: Shield },
                    { title: "Instant Lab Results", desc: "View reports as soon as they are ready.", icon: Database }
                ].map((item, i) => (
                    <div key={i} className="flex items-center gap-4 bg-white shadow-sm hover:shadow-md p-4 rounded-xl border border-gray-100 transition-all">
                        <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center text-primary">
                            <item.icon size={24} />
                        </div>
                        <div>
                            <h4 className="font-bold text-secondary">{item.title}</h4>
                            <p className="text-sm text-gray-500">{item.desc}</p>
                        </div>
                    </div>
                ))}
            </div>

            <div className="pt-4">
                <Button variant="secondary" onClick={handleGetApp}>Get Early Access</Button>
            </div>
          </div>

          <div className="flex-1 relative flex justify-center">
             <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-purple-100 rounded-full blur-3xl opacity-60"></div>
             <div className="relative z-10 animate-float-delayed transform rotate-3">
                 <PhoneMockup 
                    frameColor="light" 
                    className="shadow-[0_20px_50px_rgba(0,0,0,0.15)]"
                    imageSrc="https://2bd20b7db6dd5db900dc830b78e06be9.cdn.bubble.io/f1765371878097x327696926876986940/Screenshot_20251210_143910_BubbleGo.jpg?_gl=1*17kedxi*_gcl_au*OTYxMjAzODg4LjE3NjUzMTUwMDg.*_ga*Njg0NDczNjkxLjE3NjUzMTUwMDg.*_ga_BFPVR2DEE2*czE3NjUzMTUwMDgkbzEkZzEkdDE3NjUzNzE4NDYkajU0JGwwJGgw"
                 />
             </div>
          </div>
        </div>

      </div>
    </section>
  );
};